import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';

export interface DashboardStats {
  todaySales: {
    count: number;
    amount: number;
  };
  todayPurchases: {
    count: number;
    amount: number;
  };
  todayTransfers: {
    count: number;
  };
  totalProducts: number;
  lowStockCount: number;
  outOfStockCount: number;
  pendingInvoices: number;
  monthlyRevenue: number;
  monthlyGrowth: number;
  lastUpdated: string;
}

interface DashboardStatsResponse {
  data: DashboardStats;
}

export function useDashboardStats() {
  return useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: async () => {
      const response = await apiClient.get<DashboardStatsResponse>('/reports/dashboard');
      return response.data;
    },
    refetchInterval: 60000, // Refresh every minute
    staleTime: 30000, // Consider data stale after 30 seconds
    gcTime: 300000, // Keep in cache for 5 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

export interface SalesTrendData {
  date: string;
  sales: number;
  purchases: number;
}

interface SalesTrendResponse {
  data: SalesTrendData[];
}

export function useSalesTrend(period: 'day' | 'week' | 'month' | 'year' = 'week', outletCode?: string) {
  return useQuery({
    queryKey: ['dashboard', 'sales-trend', period, outletCode],
    queryFn: async () => {
      const response = await apiClient.get<SalesTrendResponse>('/reports/sales-trend', {
        period,
        outletCode,
      });
      // Handle both old format (labels/datasets) and new format (array)
      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }
      // Convert old format to new format
      if (data && typeof data === 'object' && 'labels' in data && 'datasets' in data) {
        const oldFormat = data as { labels: string[]; datasets: Array<{ data: number[] }> };
        const labels = oldFormat.labels;
        const salesData = oldFormat.datasets[0]?.data || [];
        const purchasesData = oldFormat.datasets[1]?.data || [];
        return labels.map((label, index) => ({
          date: label, // Will be formatted by component
          sales: salesData[index] || 0,
          purchases: purchasesData[index] || 0,
        }));
      }
      return [];
    },
    refetchInterval: 300000, // Refresh every 5 minutes
    staleTime: 60000, // Consider data stale after 1 minute
  });
}

export interface TopSellingItem {
  itemCode: string;
  itemName: string;
  quantity: number;
  revenue: number;
}

interface TopSellingResponse {
  data: TopSellingItem[];
}

export function useTopSellingItems(limit: number = 10) {
  return useQuery({
    queryKey: ['dashboard', 'top-selling', limit],
    queryFn: async () => {
      const response = await apiClient.get<TopSellingResponse>('/reports/top-selling', {
        limit,
      });
      return response.data;
    },
    refetchInterval: 300000,
    staleTime: 120000,
  });
}
